import { Archive, Banknote, ChevronRight, ClipboardList, Link2, Maximize2, MessageSquare, History, Network, ShieldAlert, ShieldCheck, ShieldX, X } from 'lucide-react';
import { type ReactNode, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { ApiError } from '../../api/errors';
import { useActiveUsers, useUserMap } from '../../api/org';
import { useProjectDetailsMany } from '../../api/projects';
import { collaboratorsOf, observersOf, useComments, useTask, useUpdateTask } from '../../api/tasks';
import type { ProjectDetails, Task } from '../../api/types';
import type { TaskCardTab } from '../../app/modules';
import { useSession } from '../../app/session';
import { TaskExtPanels, TaskExtToolbar, useTaskExtTabs } from '../../app/TaskExtensions';
import { Badge } from '../../components/ui/Badge';
import { Button, IconButton } from '../../components/ui/Button';
import { useEscapeLayer } from '../../components/ui/escape';
import { FormAlert } from '../../components/ui/Field';
import { RichTextEditor, RichTextView } from '../../components/ui/RichText';
import { Skeleton } from '../../components/ui/Skeleton';
import { ErrorState } from '../../components/ui/States';
import { Tabs } from '../../components/ui/Tabs';
import { cn } from '../../lib/cn';
import { formatDateTime } from '../../lib/dates';
import { countLabel } from '../../lib/format';
import { useLocalStorage } from '../../lib/hooks';
import { describeSensitive, detectSensitive, maskSensitive } from '../../lib/sensitive';
import { useTaskActions } from './TaskActions';
import { TaskActivity } from './TaskActivity';
import { CompleteButton, TaskFlags, TaskKey } from './TaskBits';
import { CommentBody, CommentComposer, CommentList } from './TaskComments';
import { StageTrack, TaskFieldsGrid, useTaskProjectFields } from './TaskFields';
import { TaskMenuButton } from './TaskMenu';
import { AttachmentsSection, SubtasksSection } from './TaskRelations';
import { APPROVAL_LABELS } from './taskUtils';

/**
 * Чи може користувач змінювати задачу — дзеркало перевірки сервера: власник, виконавець, автор, учасник (collaborator),
 * редактор чи адміністратор проєкту, або проєкт відкритий для всієї організації. Спостерігач — лише перегляд і коментарі.
 */
function useCanEdit(task: Task | undefined, details: Map<string, ProjectDetails>) {
  const { me, userId } = useSession();
  if (!task) return false;
  if (!me.scopes.includes('tasks:write')) return false;
  if (me.user.role === 'owner' || task.assigneeId === userId || task.createdBy === userId || collaboratorsOf(task).includes(userId)) return true;
  return task.memberships.some((m) => {
    const p = details.get(m.projectId);
    return !!p && (p.myRole === 'editor' || p.myRole === 'admin' || (p.myRole == null && p.visibility === 'org' && me.user.role !== 'guest'));
  });
}

/**
 * Назва — редагування «на місці», як в Asana: клік і пишете; збереження після паузи або втрати фокуса,
 * Enter — вийти, Esc — скасувати. Помилка сервера — під назвою.
 */
function EditableTitle({ task, canEdit, size }: { task: Task; canEdit: boolean; size: 'panel' | 'page' }) {
  const update = useUpdateTask();
  const [draft, setDraft] = useState(task.title);
  const [focused, setFocused] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLTextAreaElement>(null);
  const timer = useRef<number | undefined>(undefined);
  const sent = useRef(task.title);
  const skipBlur = useRef(false);

  useEffect(() => {
    if (!focused) {
      setDraft(task.title);
      sent.current = task.title;
    }
  }, [task.title, focused]);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${el.scrollHeight}px`;
  }, [draft, size, canEdit]);

  const save = (value: string) => {
    window.clearTimeout(timer.current);
    const v = value.replace(/\s+/g, ' ').trim();
    if (!v || v === sent.current) return;
    sent.current = v;
    update.mutate(
      { task, body: { title: v } },
      {
        onSuccess: () => setError(null),
        onError: (e) => {
          sent.current = task.title;
          setError(e instanceof ApiError ? (e.fieldError('title') ?? e.detail) : 'Назву не збережено. Спробуйте ще раз.');
        },
      },
    );
  };

  useEscapeLayer(focused, () => {
    window.clearTimeout(timer.current);
    skipBlur.current = true;
    setDraft(task.title);
    setError(null);
    ref.current?.blur();
  });

  const cls = size === 'page' ? 'text-display' : 'text-heading';
  if (!canEdit || task.archivedAt)
    return <h2 className={cn(cls, 'font-semibold tracking-[-0.01em] text-fg', task.status === 'completed' && 'text-fg-2')}>{task.title}</h2>;
  return (
    <div data-tour="task-title">
      <h2 className="sr-only">{task.title}</h2>
      <textarea
        ref={ref}
        aria-label="Назва задачі"
        aria-invalid={error ? true : undefined}
        rows={1}
        value={draft}
        maxLength={500}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          if (skipBlur.current) {
            skipBlur.current = false;
            return;
          }
          if (!draft.trim()) {
            setDraft(task.title);
            setError('Назва не може бути порожньою — повернуто попередню.');
            return;
          }
          save(draft);
        }}
        onChange={(e) => {
          const v = e.target.value.replace(/\n/g, ' ');
          setDraft(v);
          window.clearTimeout(timer.current);
          if (v.trim()) timer.current = window.setTimeout(() => save(v), 900);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            ref.current?.blur();
          }
        }}
        className={cn(
          cls,
          '-mx-2 block w-[calc(100%+16px)] resize-none overflow-hidden rounded-sm border border-transparent bg-transparent px-2 py-1 font-semibold tracking-[-0.01em] text-fg outline-none',
          'hover:border-line focus:border-line-input focus:bg-surface',
          task.status === 'completed' && 'text-fg-2',
        )}
      />
      {error && (
        <p role="alert" className="mt-1 text-meta text-danger-fg">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Опис — редактор із форматуванням прямо в картці: жирний, курсив, списки, посилання, @згадки, «/» — швидкі вставки.
 * Автозбереження; чутливі дані (картки, IBAN, паролі) не зберігаються без явного рішення.
 */
function Description({ task, canEdit }: { task: Task; canEdit: boolean }) {
  const update = useUpdateTask();
  const users = useActiveUsers();
  const mentionUsers = useMemo(() => users.map((u) => ({ id: u.id, name: u.name, avatarUrl: u.avatarUrl })), [users]);
  const names = useMemo(() => mentionUsers.map((u) => u.name), [mentionUsers]);
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const savedTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(savedTimer.current), []);
  useEffect(() => {
    setPending(null);
    setError(null);
    setState('idle');
  }, [task.id]);

  const save = (md: string, allowSensitive = false) => {
    if (!allowSensitive && detectSensitive(md).length) {
      setPending(md);
      return;
    }
    setPending(null);
    setState('saving');
    update.mutate(
      { task, body: { description: md || null, allowSensitive: allowSensitive || undefined } },
      {
        onSuccess: () => {
          setError(null);
          setState('saved');
          window.clearTimeout(savedTimer.current);
          savedTimer.current = window.setTimeout(() => setState((s) => (s === 'saved' ? 'idle' : s)), 1800);
        },
        onError: (e) => {
          setState('error');
          setError(e instanceof ApiError ? e : null);
          if (e instanceof ApiError && e.isSensitive) setPending(md);
        },
      },
    );
  };

  const status = state === 'saving' ? 'Зберігається…' : state === 'saved' ? 'Збережено' : state === 'error' ? 'Не збережено' : null;
  const found = pending ? detectSensitive(pending) : [];

  return (
    <section aria-label="Опис" data-tour="task-description">
      <h3 className="mb-1 text-meta font-semibold text-fg-2">Опис</h3>
      {canEdit && !task.archivedAt ? (
        <RichTextEditor
          value={task.description ?? ''}
          onSave={(md) => save(md)}
          label="Опис задачі"
          placeholder="Що саме потрібно зробити? Очікуваний результат, посилання на документи… «/» — вставки, «@» — згадати колегу."
          users={mentionUsers}
          status={status}
          className="-mx-2.5"
          footer={
            <>
              {pending && (
                <FormAlert
                  tone="warning"
                  title="Схоже, в описі є чутливі дані"
                  detail={`${found.length ? `Виявлено: ${describeSensitive(found)}. ` : ''}Замаскуйте їх або перенесіть у захищене місце. Опис поки не збережено.`}
                >
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="secondary" onClick={() => save(maskSensitive(pending))}>
                      Замаскувати й зберегти
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => save(pending, true)}>
                      Зберегти як є
                    </Button>
                  </div>
                </FormAlert>
              )}
              {error && !error.isSensitive && <FormAlert detail={error.detail} items={error.messagesExcept([])} />}
            </>
          }
        />
      ) : task.description ? (
        <RichTextView markdown={task.description} names={names} />
      ) : (
        <p className="text-meta text-fg-3">Опису немає.</p>
      )}
    </section>
  );
}

function ApprovalBar({ task }: { task: Task }) {
  const { userId, isOwner } = useSession();
  const actions = useTaskActions();
  const users = useUserMap();
  const approver = task.assigneeId ? users.get(task.assigneeId) : undefined;
  const canDecide = (task.assigneeId === userId || isOwner) && task.approvalStatus === 'pending';
  const status = task.approvalStatus ?? 'pending';
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border border-line bg-surface-2 px-3 py-2.5">
      <ShieldCheck className="size-4 shrink-0 text-fg-2" aria-hidden />
      <div className="min-w-0 flex-1 text-meta">
        <p className="font-semibold text-fg">Погодження · {APPROVAL_LABELS[status]}</p>
        <p className="text-fg-2">{status === 'pending' ? `Рішення ухвалює ${approver?.name ?? 'призначений погоджувач'}.` : 'Рішення зафіксовано в історії задачі.'}</p>
      </div>
      {canDecide && (
        <div className="flex flex-wrap gap-1.5">
          <Button size="sm" variant="success" icon={<ShieldCheck className="size-3.5" aria-hidden />} onClick={() => actions.decide(task, 'approved')}>
            Погодити
          </Button>
          <Button size="sm" icon={<ShieldAlert className="size-3.5" aria-hidden />} onClick={() => actions.decide(task, 'changes_requested')}>
            На доопрацювання
          </Button>
          <Button size="sm" variant="danger-ghost" icon={<ShieldX className="size-3.5" aria-hidden />} onClick={() => actions.decide(task, 'rejected')}>
            Відхилити
          </Button>
        </div>
      )}
    </div>
  );
}

function Banners({ task }: { task: Task }) {
  const actions = useTaskActions();
  const users = useUserMap();
  if (task.archivedAt)
    return (
      <div className="flex items-center gap-2 rounded-md border border-line bg-surface-2 px-3 py-2 text-meta text-fg-2">
        <Archive className="size-4" aria-hidden />
        Задачу архівовано {formatDateTime(task.archivedAt)}. Зміни неможливі; історія збережена.
      </div>
    );
  return (
    <>
      {task.subtype === 'approval' && <ApprovalBar task={task} />}
      {task.status === 'completed' && task.subtype !== 'approval' && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-success-line bg-success-soft px-3 py-2 text-meta text-success-fg">
          <span className="min-w-0 flex-1">
            Завершено {task.completedAt ? formatDateTime(task.completedAt) : ''}
            {task.completedBy ? ` · ${users.get(task.completedBy)?.name ?? ''}` : ''}. Результат — у коментарях.
          </span>
          <Button size="sm" variant="secondary" onClick={() => actions.reopen(task)}>
            Відкрити знову
          </Button>
        </div>
      )}
    </>
  );
}

/**
 * Другорядні реквізити (залежності, час, пріоритет, теги, учасники, поля проєкту, хто створив) — згорнуті,
 * доки не знадобляться: у згорнутому стані рядок підказує, що всередині вже заповнено. Вибір пам'ятає браузер.
 */
function MoreFields({ task, canEdit }: { task: Task; canEdit: boolean }) {
  const [open, setOpen] = useLocalStorage<boolean>('ers.task.more-fields', false);
  const id = useId();
  const filled = [
    task.priority !== 'normal' || task.urgent ? 'пріоритет' : null,
    task.dependsOnIds.length ? countLabel(task.dependsOnIds.length, { one: 'залежність', few: 'залежності', many: 'залежностей' }) : null,
    task.tagIds.length ? countLabel(task.tagIds.length, { one: 'тег', few: 'теги', many: 'тегів' }) : null,
    collaboratorsOf(task).length ? countLabel(collaboratorsOf(task).length, { one: 'учасник', few: 'учасники', many: 'учасників' }) : null,
    observersOf(task).length ? countLabel(observersOf(task).length, { one: 'спостерігач', few: 'спостерігачі', many: 'спостерігачів' }) : null,
  ].filter(Boolean);
  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((o) => !o)}
        className="-ml-1.5 inline-flex min-h-[var(--control-sm)] max-w-full items-center gap-1.5 rounded-sm px-1.5 text-left text-meta font-medium text-fg-2 hover:bg-surface-2 hover:text-fg"
      >
        <ChevronRight className={cn('ers-turn size-4 shrink-0 text-fg-3', open && 'rotate-90')} aria-hidden />
        <span className="shrink-0">{open ? 'Згорнути реквізити' : 'Ще реквізити'}</span>
        {!open && <span className="min-w-0 truncate font-normal text-fg-3">{filled.length ? filled.join(' · ') : 'залежності, час, пріоритет, теги, учасники'}</span>}
      </button>
      {open && (
        <div id={id} className="mt-1">
          <TaskFieldsGrid task={task} canEdit={canEdit} part="secondary" />
        </div>
      )}
    </div>
  );
}

/** Результат завершеної задачі — окремим блоком під описом (він же лишається в обговоренні). */
function ResultBlock({ task }: { task: Task }) {
  const q = useComments(task.id);
  const users = useUserMap();
  const result = useMemo(() => [...(q.data ?? [])].reverse().find((c) => c.kind === 'result' && !c.deletedAt), [q.data]);
  if (task.status !== 'completed' || !result) return null;
  const author = result.authorId ? users.get(result.authorId) : undefined;
  return (
    <section aria-label="Результат" className="rounded-md border border-success-line bg-success-soft/50 px-3 py-2.5">
      <h3 className="mb-1 text-meta font-semibold text-success-fg">Результат</h3>
      <CommentBody body={result.body} mentioned={result.mentions.map((mid) => ({ id: mid, name: users.get(mid)?.name ?? '' })).filter((m) => m.name)} />
      <p className="mt-1 text-caption text-fg-3">
        {author?.name ?? 'ЄРС'} · {formatDateTime(result.createdAt)}
      </p>
    </section>
  );
}

/** Вкладка «Фінанси»: фінансові поля задачі (сума угоди, плановані витрати) й фінансові розділи модулів. */
function FinanceTab({ task, canEdit }: { task: Task; canEdit: boolean }) {
  const { finance, financeMissing } = useTaskProjectFields(task);
  return (
    <div className="flex flex-col gap-5">
      <section aria-label="Фінансові поля">
        <h3 className="mb-1 text-meta font-semibold text-fg-2">Фінансові поля</h3>
        {finance.length ? (
          <>
            {financeMissing > 0 && (
              <p role="note" className="mb-2 rounded-md border border-info-line bg-info-soft px-3 py-2 text-meta text-info-fg">
                Поточний етап вимагає заповнити {countLabel(financeMissing, { one: 'фінансове поле', few: 'фінансові поля', many: 'фінансових полів' })} — без цього задачу не перемістити далі.
              </p>
            )}
            <TaskFieldsGrid task={task} canEdit={canEdit} part="finance" />
          </>
        ) : (
          <p className="text-meta text-fg-3">У проєктах цієї задачі немає фінансових полів.</p>
        )}
        <p className="mt-2 text-caption text-fg-3">Ці дані бачать лише власник і працівники з фінансовою роллю.</p>
      </section>
      <TaskExtPanels task={task} tab="finance" />
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-4 p-5" role="status" aria-label="Завантаження задачі">
      <Skeleton className="h-3 w-24" />
      <Skeleton className="h-6 w-4/5" />
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="flex gap-3">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-4" style={{ width: `${30 + i * 9}%` }} />
        </div>
      ))}
      <Skeleton className="mt-4 h-20 w-full" />
    </div>
  );
}

type Props = {
  taskId: string;
  mode: 'panel' | 'page';
  onClose?: () => void;
  onOpenPage?: () => void;
  headerExtra?: ReactNode;
};

/** Картка задачі: спільна для бічної панелі та окремої сторінки. */
export function TaskDetail({ taskId, mode, onClose, onOpenPage }: Props) {
  const q = useTask(taskId);
  const task = q.data;
  const projectIds = useMemo(() => task?.memberships.map((m) => m.projectId) ?? [], [task?.memberships]);
  const details = useProjectDetailsMany(projectIds);
  const canEdit = useCanEdit(task, details);

  if (q.isLoading) return <DetailSkeleton />;
  if (q.error || !task)
    return (
      <div className="flex h-full flex-col">
        {onClose && (
          <div className="flex justify-end p-2">
            <IconButton label="Закрити" onClick={onClose}>
              <X className="size-4" aria-hidden />
            </IconButton>
          </div>
        )}
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      </div>
    );
  // Нова задача — картка з початку: вкладка «Робота», обговорення, прокручування вгору.
  return <TaskCard key={task.id} task={task} details={details} canEdit={canEdit} mode={mode} onClose={onClose} onOpenPage={onOpenPage} />;
}

/**
 * Уміст картки. Вкладки: «Робота» — те, що потрібно виконавцю (головні реквізити, опис, результат, чек-лист,
 * підзадачі, вкладення, обговорення); «Управління» — управлінський шар (лише колу й учасникам одиниці);
 * «Фінанси» — фінансові поля й витрати (лише з finance:read). Якщо доступна сама «Робота», вкладок немає.
 */
function TaskCard({ task, details, canEdit, mode, onClose, onOpenPage }: { task: Task; details: Map<string, ProjectDetails>; canEdit: boolean; mode: 'panel' | 'page'; onClose?: () => void; onOpenPage?: () => void }) {
  const actions = useTaskActions();
  const { can } = useSession();
  const ext = useTaskExtTabs(task);
  const { financeMissing } = useTaskProjectFields(task);
  const [cardTab, setCardTab] = useState<TaskCardTab>('work');
  const [tab, setTab] = useState<'comments' | 'activity'>('comments');

  const cardTabs = [
    { value: 'work' as const, label: 'Робота', icon: <ClipboardList className="size-4" aria-hidden /> },
    ...(ext.management ? [{ value: 'management' as const, label: 'Управління', icon: <Network className="size-4" aria-hidden /> }] : []),
    ...(can('finance:read') ? [{ value: 'finance' as const, label: 'Фінанси', icon: <Banknote className="size-4" aria-hidden />, count: financeMissing || undefined }] : []),
  ];
  // Вкладка могла зникнути (змінилися права чи дані задачі) — повертаємося до «Роботи».
  const current: TaskCardTab = cardTabs.some((t) => t.value === cardTab) ? cardTab : 'work';

  const primary = task.memberships[0] ? details.get(task.memberships[0].projectId) : undefined;
  const primarySection = primary?.sections.find((s) => s.id === task.memberships[0]?.sectionId);
  const processProject = task.memberships.map((m) => details.get(m.projectId)).find((p) => p?.type === 'process') ?? primary;

  const header = (
    <div className="flex h-12 shrink-0 items-center gap-2 border-b border-line bg-surface px-3 sm:px-4">
      <CompleteButton task={task} onClick={() => actions.toggle(task)} disabled={!canEdit || !!task.archivedAt} />
      <TaskKey task={task} className="text-body" />
      {primary && (
        <nav aria-label="Розташування" className="hidden min-w-0 items-center gap-1 text-meta text-fg-3 sm:flex">
          <ChevronRight className="size-3.5 shrink-0" aria-hidden />
          <Link to={`/projects/${primary.id}`} className="truncate hover:text-fg hover:underline">
            {primary.name}
          </Link>
          {primarySection && (
            <>
              <ChevronRight className="size-3.5 shrink-0" aria-hidden />
              <span className="truncate">{primarySection.name}</span>
            </>
          )}
        </nav>
      )}
      <span className="ml-auto flex items-center gap-0.5">
        <span className="mr-1 flex items-center">
          <TaskExtToolbar task={task} />
        </span>
        {/* На телефоні рядок вузький: посилання й «на всю сторінку» є в меню «…», а кнопка закриття має лишатися видимою. */}
        <span className="hidden sm:contents">
          <IconButton label="Копіювати посилання" size="sm" onClick={() => actions.copyLink(task)}>
            <Link2 className="size-4" aria-hidden />
          </IconButton>
          {mode === 'panel' && onOpenPage && (
            <span data-tour="task-expand">
              <IconButton label="Розгорнути на всю сторінку" size="sm" onClick={onOpenPage}>
                <Maximize2 className="size-4" aria-hidden />
              </IconButton>
            </span>
          )}
        </span>
        <TaskMenuButton task={task} inPanel />
        {onClose && (
          <IconButton label="Закрити картку" shortcut="Esc" size="sm" onClick={onClose}>
            <X className="size-4" aria-hidden />
          </IconButton>
        )}
      </span>
    </div>
  );

  /** Головні реквізити й згорнута решта — у вузькій картці; на широкій сторінці всі реквізити стоять збоку. */
  const inlineFields = (
    <div className="flex flex-col gap-1" data-tour="task-fields">
      <TaskFieldsGrid task={task} canEdit={canEdit} part="primary" />
      <MoreFields task={task} canEdit={canEdit} />
    </div>
  );
  const discussion = (
    <section aria-label="Обговорення та історія" className="border-t border-line pt-3" data-tour="task-comments">
      <Tabs
        label="Обговорення та історія"
        size="sm"
        value={tab}
        onChange={setTab}
        tabs={[
          { value: 'comments', label: 'Коментарі', count: task.commentCount, icon: <MessageSquare className="size-3.5" aria-hidden /> },
          { value: 'activity', label: 'Історія змін', icon: <History className="size-3.5" aria-hidden /> },
        ]}
      />
      <div className="mt-1" role="tabpanel">
        {tab === 'comments' ? <CommentList task={task} /> : <TaskActivity task={task} />}
      </div>
    </section>
  );
  const work = (
    <>
      <Description task={task} canEdit={canEdit} />
      <ResultBlock task={task} />
      <TaskExtPanels task={task} tab="work" only="filled" />
      <SubtasksSection task={task} canEdit={canEdit && !task.archivedAt} />
      <AttachmentsSection task={task} />
      <TaskExtPanels task={task} tab="work" only="empty" />
      {discussion}
    </>
  );
  const sectionTabs =
    cardTabs.length > 1 ? (
      <div className="border-b border-line">
        <Tabs label="Розділи картки задачі" value={current} onChange={setCardTab} tabs={cardTabs} className="-mb-px" />
      </div>
    ) : null;
  const management = <TaskExtPanels task={task} tab="management" flat />;
  const finance = <FinanceTab task={task} canEdit={canEdit} />;
  const composerVisible = current === 'work' && tab === 'comments' && !task.archivedAt;

  const readOnlyNote =
    !canEdit && !task.archivedAt ? (
      <p role="note" className="rounded-md border border-info-line bg-info-soft px-3 py-2 text-meta text-info-fg">
        Ви переглядаєте задачу як спостерігач: коментувати можна, а змінювати — виконавцю, учасникам і редакторам проєкту.
      </p>
    ) : null;

  const titleBlock = (
    <div className="flex flex-col gap-2">
      <EditableTitle task={task} canEdit={canEdit} size={mode} />
      <div className="flex flex-wrap items-center gap-1.5">
        <TaskFlags task={task} />
        {task.status === 'completed' && <Badge tone="success">Завершено</Badge>}
      </div>
    </div>
  );

  if (mode === 'page') {
    return (
      <div className="flex min-h-full flex-col">
        {header}
        <div className="mx-auto grid w-full max-w-[1200px] gap-6 px-4 py-5 sm:px-6 xl:grid-cols-[minmax(0,1fr)_380px]">
          <div className="flex min-w-0 flex-col gap-6">
            <Banners task={task} />
            {readOnlyNote}
            {titleBlock}
            {processProject && <StageTrack task={task} project={processProject} disabled={!canEdit || !!task.archivedAt} />}
            {sectionTabs}
            {current === 'work' && (
              <>
                <div className="xl:hidden">{inlineFields}</div>
                {work}
              </>
            )}
            {current === 'management' && management}
            {current === 'finance' && finance}
            {composerVisible && <CommentComposer task={task} />}
          </div>
          <aside aria-label="Реквізити задачі" className="hidden xl:block">
            <div className="sticky top-4 rounded-md border border-line bg-surface p-4">
              <TaskFieldsGrid task={task} canEdit={canEdit} part="all" />
            </div>
          </aside>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {header}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <div className="flex flex-col gap-5 px-4 py-4 sm:px-5">
          <Banners task={task} />
          {readOnlyNote}
          {titleBlock}
          {processProject && <StageTrack task={task} project={processProject} disabled={!canEdit || !!task.archivedAt} />}
          {sectionTabs}
          {current === 'work' && (
            <>
              {inlineFields}
              {work}
            </>
          )}
          {current === 'management' && management}
          {current === 'finance' && finance}
        </div>
      </div>
      {composerVisible && (
        <div className="shrink-0 border-t border-line bg-surface px-4 py-2.5 pb-[max(10px,env(safe-area-inset-bottom))] sm:px-5">
          <CommentComposer task={task} />
        </div>
      )}
    </div>
  );
}
